import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEO/SEOHead";
import Breadcrumbs from "../components/SEO/Breadcrumbs";
import { getWebSiteSchema, getOrganizationSchema, getTravelAgencySchema } from "../components/SEO/schemaTemplates";
import { getHotelListings } from "../services/hotelService";
import CurrencyService from "../services/currencyService";
import { FaStar, FaMapMarkerAlt, FaSearch, FaBed, FaCheckCircle, FaChevronRight } from "react-icons/fa";
import "./HotelsHubPage.css";

const DESTINATION_META = [
  { city: "All Destinations", slug: "all" },
  { city: "Colombo", slug: "colombo", desc: "Central city hotels & oceanfront suites" },
  { city: "Ella", slug: "ella", desc: "Mountain view villas & misty hill chalets" },
  { city: "Sigiriya", slug: "sigiriya", desc: "Eco-lodges near the ancient Lion Rock" },
  { city: "Galle", slug: "galle", desc: "Colonial boutique villas inside the Dutch Fort" },
  { city: "Kandy", slug: "kandy", desc: "Hillside resorts overlooking the sacred lake" },
  { city: "Mirissa", slug: "mirissa", desc: "Beachfront cabanas & whale watching retreats" },
  { city: "Nuwara Eliya", slug: "nuwara-eliya", desc: "Heritage tea plantation cottages" }
];

const getCityMatch = (hotel) => {
  const text = `${hotel.address || ""} ${hotel.hotelName || ""} ${hotel.listingName || ""}`.toLowerCase();
  if (text.includes("ella")) return "ella";
  if (text.includes("sigiriya") || text.includes("dambulla")) return "sigiriya";
  if (text.includes("colombo")) return "colombo";
  if (text.includes("galle") || text.includes("unawatuna")) return "galle";
  if (text.includes("kandy")) return "kandy";
  if (text.includes("mirissa") || text.includes("weligama") || text.includes("matara")) return "mirissa";
  if (text.includes("nuwara eliya") || text.includes("nuwaraeliya")) return "nuwara-eliya";
  return "other";
};

export default function HotelsHubPage() {
  const { city: routeCity } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryCity = searchParams.get("city");
  const activeCity = (routeCity || queryCity || "all").toLowerCase();
  const activeType = searchParams.get("type") || "all";

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [, setCurrency] = useState(CurrencyService.selectedCurrency);

  useEffect(() => {
    const onCurrencyChange = (e) => setCurrency(e.detail);
    window.addEventListener("currencyUpdate", onCurrencyChange);
    return () => window.removeEventListener("currencyUpdate", onCurrencyChange);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getHotelListings()
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setHotels(list);
      })
      .catch((err) => {
        console.error("Error fetching hotel listings:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Dynamic counts per destination from real database
  const destinationCounts = useMemo(() => {
    const counts = { all: hotels.length };
    DESTINATION_META.forEach((d) => {
      if (d.slug !== "all") {
        counts[d.slug] = hotels.filter((h) => getCityMatch(h) === d.slug).length;
      }
    });
    return counts;
  }, [hotels]);

  // Filter hotels based on active filters
  const filteredHotels = useMemo(() => {
    return hotels.filter((hotel) => {
      // 1. City Filter
      if (activeCity !== "all") {
        const hCity = getCityMatch(hotel);
        const rawAddress = (hotel.address || "").toLowerCase();
        const hotelName = (hotel.hotelName || "").toLowerCase();
        const cityKey = activeCity.replace("-", " ");
        const matches = hCity === activeCity || rawAddress.includes(cityKey) || hotelName.includes(cityKey);
        if (!matches) return false;
      }

      // 2. Type Filter
      if (activeType !== "all") {
        const text = `${hotel.listingName || ""} ${hotel.hotelName || ""}`.toLowerCase();
        if (activeType === "villas" && !text.includes("villa")) return false;
        if (activeType === "resorts" && !text.includes("resort") && !text.includes("beach")) return false;
        if (activeType === "eco" && !text.includes("eco") && !text.includes("lodge")) return false;
      }

      // 3. Search Filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const fullText = `${hotel.hotelName || ""} ${hotel.listingName || ""} ${hotel.address || ""}`.toLowerCase();
        if (!fullText.includes(query)) return false;
      }

      return true;
    });
  }, [hotels, activeCity, activeType, searchTerm]);

  const handleCitySelect = (slug) => {
    if (slug === "all") {
      navigate("/hotels");
    } else {
      navigate(`/hotels/${slug}`);
    }
  };

  const handleHotelClick = (hotel) => {
    navigate(`/travelerhotel/${hotel.id}`, { state: { hotel } });
  };

  const currentCityName = DESTINATION_META.find((d) => d.slug === activeCity)?.city || activeCity.replace("-", " ");

  const breadcrumbItems = [
    { name: "Sri Lanka Hotels", url: "/hotels" },
    ...(activeCity !== "all" ? [{ name: currentCityName, url: `/hotels/${activeCity}` }] : [])
  ];

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [getWebSiteSchema(), getOrganizationSchema(), getTravelAgencySchema()]
  };

  return (
    <div className="seo-page-wrapper">
      <SEOHead
        title={
          activeCity !== "all"
            ? `${currentCityName} Hotels & Stays | Verified Sri Lanka Stays | Soloway`
            : "Sri Lanka Hotel Bookings | Best Verified Stays & Villas | Soloway"
        }
        description={`Browse ${filteredHotels.length} verified stays in ${
          activeCity !== "all" ? currentCityName : "Sri Lanka"
        }. Direct owner bookings, transparent rates, zero booking fees on Soloway.`}
        keywords="sri lanka hotel, best hotels sri lanka, ella hotels, sigiriya luxury resort, colombo hotel booking, cheap stays sri lanka, mirissa beach hotel"
        canonicalUrl={activeCity !== "all" ? `/sri-lanka-hotels/${activeCity}` : "/sri-lanka-hotels"}
        structuredData={combinedSchema}
      />
      <Navbar />

      <main className="seo-page-container">
        <div className="content-inner">
          <Breadcrumbs items={breadcrumbItems} />

          {/* Page Header */}
          <header className="page-header-seo">
            <span className="seo-badge">Verified Sri Lanka Stays Database</span>
            <h1>
              {activeCity !== "all"
                ? `Hotels & Boutique Stays in ${currentCityName}`
                : "Sri Lanka Hotels, Boutique Villas & Beach Resorts"}
            </h1>
            <p className="lead-text">
              {activeCity !== "all"
                ? `Discover verified accommodations in ${currentCityName} with genuine guest reviews, instant confirmation, and local rates.`
                : "Find and book vetted Sri Lankan stays with transparent pricing, secure Payments.lk online card payment support, and verified host credentials."}
            </p>
          </header>

          {/* Search & Filter Bar */}
          <section className="hub-search-filter-section">
            <div className="hub-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search hotel name, room type, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm("")}>
                  ×
                </button>
              )}
            </div>

            {/* City Tabs */}
            <div className="city-filter-scroll">
              {DESTINATION_META.map((dest) => {
                const count = destinationCounts[dest.slug] ?? 0;
                const isSelected = activeCity === dest.slug;
                return (
                  <button
                    key={dest.slug}
                    onClick={() => handleCitySelect(dest.slug)}
                    className={`city-filter-chip ${isSelected ? "active" : ""}`}
                  >
                    <span>{dest.city}</span>
                    <span className="chip-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Live Hotel Listings Grid */}
          <section className="live-listings-section" aria-labelledby="hotels-grid-heading">
            <div className="section-title-row">
              <div>
                <h2 id="hotels-grid-heading">
                  {activeCity !== "all" ? `${currentCityName} Accommodations` : "Available Verified Hotels"}
                </h2>
                <span className="live-results-count">
                  Showing {filteredHotels.length} {filteredHotels.length === 1 ? "stay" : "stays"}
                </span>
              </div>
              <span className="total-stays-count">
                <FaCheckCircle style={{ marginRight: 4 }} /> 100% Real Database Listings
              </span>
            </div>

            {loading ? (
              <div className="hub-loading-state">
                <div className="hub-spinner"></div>
                <p>Loading real hotel listings from database...</p>
              </div>
            ) : filteredHotels.length === 0 ? (
              <div className="hub-empty-state">
                <FaBed className="empty-icon" />
                <h3>No hotels found matching your filter</h3>
                <p>Try switching to "All Destinations" or clearing your search term to see all available stays.</p>
                <button className="reset-filter-btn" onClick={() => { handleCitySelect("all"); setSearchTerm(""); }}>
                  View All Stays
                </button>
              </div>
            ) : (
              <div className="hotel-listings-grid">
                {filteredHotels.map((hotel) => {
                  const rating = parseFloat(hotel.averageRating) || 5.0;
                  const reviews = hotel.reviewCount || 0;
                  const price = parseFloat(hotel.price) || 0;
                  const convertedPrice = CurrencyService.convert(price, { fromCurrency: "LKR" });
                  const currencySymbol = CurrencyService.getCurrencySymbol(CurrencyService.selectedCurrency);
                  const cleanListingName = (hotel.listingName || "")
                    .split("@")[0]
                    .trim();

                  return (
                    <article
                      key={hotel.id}
                      className="hotel-card-premium"
                      onClick={() => handleHotelClick(hotel)}
                    >
                      <div className="hotel-image-wrapper">
                        <img
                          src={hotel.mainImage || "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&auto=format&fit=crop&q=80"}
                          alt={hotel.hotelName || "Sri Lanka Hotel"}
                          className="hotel-card-img"
                          loading="lazy"
                        />
                        <span className="verified-badge">
                          <FaCheckCircle /> Verified Host
                        </span>
                        {price > 0 && (
                          <div className="hotel-price-badge">
                            <span className="price-num">{currencySymbol} {convertedPrice.toFixed(0)}</span>
                            <span className="price-unit">/ night</span>
                          </div>
                        )}
                      </div>

                      <div className="hotel-card-body">
                        <div className="hotel-rating-row">
                          <div className="rating-pill">
                            <FaStar className="star-icon" />
                            <span>{rating > 0 ? rating.toFixed(1) : "5.0"}</span>
                          </div>
                          <span className="review-count-text">
                            {reviews > 0 ? `(${reviews} reviews)` : "(Verified Stay)"}
                          </span>
                        </div>

                        <h3 className="hotel-card-title">
                          {hotel.hotelName || cleanListingName || "SoloWay Boutique Stay"}
                        </h3>

                        {cleanListingName && cleanListingName !== hotel.hotelName && (
                          <span className="room-type-tag">{cleanListingName}</span>
                        )}

                        <p className="hotel-location-text">
                          <FaMapMarkerAlt className="location-icon" />
                          <span>{hotel.address || "Sri Lanka"}</span>
                        </p>

                        <div className="hotel-card-footer">
                          <span className="availability-tag">Instant Confirmation</span>
                          <button className="view-hotel-btn">
                            View Hotel <FaChevronRight style={{ fontSize: "0.75rem", marginLeft: 4 }} />
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* Popular Sri Lanka Destinations Hub-and-Spoke */}
          <section className="destinations-grid-section" aria-labelledby="destination-heading">
            <div className="section-title-row">
              <h2 id="destination-heading">Explore by Sri Lanka Travel Hub</h2>
              <span className="total-stays-count">Direct Local Rates</span>
            </div>

            <div className="destinations-grid">
              {DESTINATION_META
                .filter((d) => d.slug !== "all")
                .map((item) => (
                  <article key={item.slug} className="city-card">
                    <div className="city-card-header">
                      <span className="city-price-tag">
                        {destinationCounts[item.slug] || 0} Listed Stays
                      </span>
                    </div>
                    <h3>
                      <Link to={`/hotels/${item.slug}`} className="city-card-title-link">
                        Hotels in {item.city}
                      </Link>
                    </h3>
                    <p>{item.desc}</p>
                    <div className="city-card-footer">
                      <span className="listing-count">Instant Booking</span>
                      <Link to={`/hotels/${item.slug}`} className="view-link">
                        Explore {item.city} &rarr;
                      </Link>
                    </div>
                  </article>
                ))}
            </div>
          </section>

          {/* Informational Rich Content Section */}
          <article className="seo-rich-content">
            <h2>Ultimate Guide to Finding the Best Sri Lanka Hotel</h2>
            <p>
              Sri Lanka is a year-round tropical paradise with distinct monsoon seasons influencing where to stay. When booking a <strong>Sri Lanka hotel</strong> on Soloway:
            </p>
            <ul>
              <li><strong>South & West Coast (November to April):</strong> Peak season for Galle, Mirissa, Weligama, and Bentota. Ideal for beach resorts, surfing, and whale watching.</li>
              <li><strong>East Coast (May to September):</strong> Best sunshine and dry weather for Arugam Bay, Passikudah, and Trincomalee.</li>
              <li><strong>Central Hill Country (Year-round):</strong> Ella, Kandy, and Nuwara Eliya offer refreshing cooler climates, hiking trails, and heritage tea bungalow stays.</li>
            </ul>
            <p>
              Soloway offers direct communication with local hotel owners, zero hidden checkout fees, and 100% verified traveler reviews.
            </p>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
